/**
 * Set Shop section(s) — live price/badge refresh.
 *
 * Everything else on this section (variant selection state, the hidden
 * variant id input, sold-out disabling of the Add to cart button, and the
 * actual AJAX add-to-cart + cart drawer/notification integration) is
 * handled entirely by the theme's own existing custom elements:
 * loess-variant-picker, loess-variant-radios/selects, and
 * loess-product-form. Nothing here talks to the cart.
 *
 * The one thing the theme's built-in variant picker can't do for us is
 * refresh the on-page price/badges: it normally does that by re-fetching
 * the real product page's own section (`?section_id=...`), but each
 * product block on this page uses a synthetic section id (always
 * prefixed "set-shop-", see snippets/set-shop-item.liquid) so several
 * products can live on one page without colliding. So this file listens
 * for the theme's own `product:variant:changed` event (dispatched by
 * loess-variant-picker on every option change) and updates the price
 * text + sale/sold-out badges for that one product block.
 *
 * On the product-page version of this section
 * (sections/main-product-set-shop.liquid) our product rows sit nested
 * inside the theme's own <loess-product> wrapper (so they land in the
 * "right column" alongside the title/description, per the native
 * two-column product layout). <loess-product> also listens for
 * `product:variant:changed` to sync its hero gallery to the changed
 * variant's image — using the *real* product page's section id, which
 * our synthetic id never matches. Left alone that would just no-op
 * harmlessly, but to be safe we intercept in the capture phase (before
 * the event ever reaches <loess-product>) and stop it from propagating
 * any further once we've handled it ourselves — but only for events
 * carrying our own "set-shop-" prefixed section id, so nothing else on
 * the page (a real product page's own variant picker, another app,
 * etc.) is ever affected.
 *
 * Cart drawer connection (not just a redirect setting):
 * loess-product-form finds "its" cart drawer/notification element once,
 * in its own constructor, by searching the whole document for
 * loess-cart-notification / loess-cart-drawer-items. If that search runs
 * before the theme's global cart drawer markup (rendered near the end of
 * <body>, see layout/theme.liquid) has been parsed yet, the form is left
 * thinking there's no drawer to open and falls back to a full navigation
 * to /cart -- even though the "Add to cart" flow setting is "drawer" and
 * the item is added successfully. This can happen depending on exactly
 * when/where our product blocks get parsed relative to the rest of the
 * page (e.g. nested inside the product page's two-column layout, or
 * inside the theme editor's own preview wrapper). So once the full page
 * has loaded, we double-check every one of our own product forms and, if
 * one came up without a cart reference, hand it the drawer/notification
 * element directly -- a pure safety net, never overrides a form that
 * already found its own cart correctly.
 */
(function () {
  if (window.SetShop && window.SetShop.installed) return;

  window.SetShop = window.SetShop || {};
  window.SetShop.installed = true;

  function formatWithDelimiters(number, precision, thousands, decimal) {
    precision = isNaN(precision) ? 2 : precision;
    thousands = thousands === undefined ? ',' : thousands;
    decimal = decimal === undefined ? '.' : decimal;

    if (isNaN(number)) {
      number = 0;
    }

    number = (number / 100.0).toFixed(precision);

    var parts = number.split('.');
    var dollarsAmount = parts[0].replace(/(\d)(?=(\d\d\d)+(?!\d))/g, '$1' + thousands);
    var centsAmount = parts[1] ? decimal + parts[1] : '';

    return dollarsAmount + centsAmount;
  }

  function formatMoney(cents, format) {
    if (typeof cents === 'string') {
      cents = cents.replace('.', '');
    }

    var value = '';
    var placeholderRegex = /\{\{\s*(\w+)\s*\}\}/;
    var formatString = format || '${{amount}}';

    var match = formatString.match(placeholderRegex);
    var key = match ? match[1] : 'amount';

    switch (key) {
      case 'amount_no_decimals':
        value = formatWithDelimiters(cents, 0);
        break;
      case 'amount_with_comma_separator':
        value = formatWithDelimiters(cents, 2, '.', ',');
        break;
      case 'amount_no_decimals_with_comma_separator':
        value = formatWithDelimiters(cents, 0, '.', ',');
        break;
      case 'amount_with_apostrophe_separator':
        value = formatWithDelimiters(cents, 2, "'", '.');
        break;
      default:
        value = formatWithDelimiters(cents, 2);
    }

    return formatString.replace(placeholderRegex, value);
  }

  window.SetShop.formatMoney = formatMoney;

  function updatePrice(sectionId, variant) {
    var wrapper = document.getElementById('SetShopPrice-' + sectionId);

    if (!wrapper || !variant) return;

    var useCurrencyCode = wrapper.dataset.currencyShowCodes === 'true';
    var format = useCurrencyCode
      ? wrapper.dataset.moneyWithCurrencyFormat
      : wrapper.dataset.moneyFormat;

    var priceText = formatMoney(variant.price, format);
    var onSale = !!(
      variant.compare_at_price &&
      variant.compare_at_price > variant.price
    );

    var priceRoot = wrapper.querySelector('.price');
    var regularEl = wrapper.querySelector('.price__regular .price-item--regular');
    var saleEl = wrapper.querySelector('.price__sale .price-item--sale');
    var compareEl = wrapper.querySelector('.price__sale .price-item--last');

    if (priceRoot) {
      priceRoot.classList.toggle('price--on-sale', onSale);
      priceRoot.classList.toggle('price--sold-out', !variant.available);
    }

    if (regularEl) regularEl.textContent = priceText;
    if (saleEl) saleEl.textContent = priceText;

    if (compareEl && variant.compare_at_price) {
      compareEl.textContent = formatMoney(variant.compare_at_price, format);
    }
  }

  function updateBadges(sectionId, variant) {
    var wrapper = document.getElementById('SetShopBadges-' + sectionId);

    if (!wrapper || !variant) return;

    var soldOutBadge = wrapper.querySelector('[data-badge="sold-out"]');
    var saleBadge = wrapper.querySelector('[data-badge="sale"]');
    var onSale = !!(
      variant.compare_at_price &&
      variant.compare_at_price > variant.price &&
      variant.available
    );

    if (soldOutBadge) soldOutBadge.hidden = !!variant.available;
    if (saleBadge) saleBadge.hidden = !onSale;
  }

  document.addEventListener(
    'product:variant:changed',
    function (event) {
      if (!event.detail) return;

      var sectionId = event.detail.sectionId;
      var variant = event.detail.variant;

      if (!sectionId || sectionId.indexOf('set-shop-') !== 0 || !variant) return;

      updatePrice(sectionId, variant);
      updateBadges(sectionId, variant);

      // Handled — stop before it reaches an ancestor <loess-product>
      // (product page hero gallery), which only knows how to sync
      // against the real product page's own section id.
      event.stopPropagation();
    },
    true
  );

  function connectCartDrawer() {
    var cartEl = document.querySelector('loess-cart-notification') || document.querySelector('loess-cart-drawer-items');

    if (!cartEl) return;

    document.querySelectorAll('loess-product-form').forEach(function (formEl) {
      var innerForm = formEl.querySelector('form');
      var formId = innerForm ? innerForm.getAttribute('id') || '' : '';

      // Only ever touch our own set-shop product blocks — never a real
      // product page's own buy button, a quick-add card, etc.
      if (formId.indexOf('product-form-set-shop-') !== 0) return;

      if (!formEl.cart) {
        formEl.cart = cartEl;
      }
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', connectCartDrawer);
  } else {
    connectCartDrawer();
  }
})();
